import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import {
  getLocalizedName,
  getOrderClass,
  getOrderItemTotal,
  getSatisfiedOrderCondition,
} from '../../../../utility/util/util.service';
import { connect } from 'react-redux';
import {
  updateCartItem,
  addCartItem,
} from '../../../../redux/store/store.actions';
import QtySelector from '../../qty-selector/qty-selector.component';
import { updateCartItemQty } from './cart.service';
import { notification } from '../../../../utility/notification/notification.service';
import LazyImage from '../../lazyload/lazy-image/lazy-image.component';
import * as Icon from 'react-feather';
import { SortableHandle } from 'react-sortable-hoc';
import { Menu } from 'react-feather';
import classnames from 'classnames';
import Marquee from '../../helpers/marquee.component';

const OrderItemLine = ({
  orderItem, // mandatory
  context, // one of "cart", "shared_cart", "course_cart", "order"
  cart, // required if context is NOT "order"
  store, // required if context is NOT "order"
  addCartItem, // required if context is "shared_cart"
  updateCartItem, // required if context is "cart", "course_cart"
  checkout, // required if context is NOT "order"
  handleEditItemAdditions, // required if context is "cart", "course_cart"
}) => {
  const intl = useIntl();
  const { hash, itemData, additions, qty } = orderItem || {};
  const availableGroups = useMemo(
    () =>
      [...(itemData?.additionGroups || [])]
        // check if at least one entry has posIntegrationRef
        .filter(
          ({ entries }) =>
            !store?.merchantProfile?.posIntegration ||
            entries.some(({ addition }) => !!addition.posIntegrationRef),
        )
        // check OrderConditions on ItemAdditionGroup level
        .filter(
          ({ orderConditions }) =>
            !orderConditions?.length ||
            getSatisfiedOrderCondition(
              orderConditions,
              getOrderClass(checkout?.orderType),
            ),
        ),
    [itemData?.additionGroups, store?.merchantProfile?.posIntegration],
  );

  const SortableDragHandle = SortableHandle(() => (
    <span className="pr-1">
      <Menu />
    </span>
  ));

  return orderItem ? (
    <div className="d-flex align-items-center mb-1">
      {context === 'course_cart' && <SortableDragHandle />}
      <div
        onClick={() =>
          ['cart', 'course_cart'].includes(context) &&
          availableGroups?.length > 0 &&
          handleEditItemAdditions(orderItem)
        }
      >
        <LazyImage
          image={itemData?.image}
          src={
            itemData?.image === null &&
            '/assets/img/default_item_image_thumbnail.svg'
          }
          variant="thumbnail"
          className={classnames(
            'img-rounded flex-grow-0',
            { 'thumbnail-50': context !== 'order' },
            { 'thumbnail-40': context === 'order' },
          )}
          placeholderContainerClassNames={
            context === 'order' ? 'thumbnail-40' : 'thumbnail-50'
          }
          placeholderClassNames={
            context === 'order' ? 'thumbnail-40' : 'thumbnail-50'
          }
        />
      </div>
      <div
        onClick={() =>
          ['cart', 'course_cart'].includes(context) &&
          availableGroups?.length > 0 &&
          handleEditItemAdditions(orderItem)
        }
        className="flex-grow-1 d-flex flex-column px-75"
      >
        <div className="font-medium-1 black line-height-125">
          <Marquee>
            {['order'].includes(context) && qty > 1 && (
              <span>{`${qty}x `}</span>
            )}
            {getLocalizedName(itemData, intl.locale)}
          </Marquee>
        </div>
        <div className="font-small-2 mt-25">
          {additions?.length > 0 ? (
            <div>
              {additions
                ?.map(
                  (addition) =>
                    (addition.qty > 1 ? `${addition.qty}x ` : '') +
                    getLocalizedName(
                      context === 'order'
                        ? addition.itemAddition
                        : addition.additionGroupEntry?.addition,
                      intl.locale,
                    ),
                )
                .join(', ')}
            </div>
          ) : (
            ['cart', 'course_cart'].includes(context) &&
            availableGroups?.length > 0 && (
              <span>
                <FormattedMessage id="addAdditionsLabel" />
              </span>
            )
          )}
        </div>
      </div>
      <div className="flex-grow-0 d-flex flex-column justify-content-between align-items-end">
        {context === 'shared_cart' ? (
          <>
            <span className="font-medium-1 mb-25 black">
              <FormattedNumber
                value={getOrderItemTotal(checkout.orderType, orderItem, true)}
                format="priceCurrency"
              />
            </span>
            <Icon.Copy
              size={22}
              className="primary ml-50"
              onClick={() => {
                addCartItem(cart, itemData, additions, 1, checkout.orderType);
                notification('success', 'copiedItemSuccess');
              }}
            />
          </>
        ) : (
          <>
            <span
              className={classnames('font-medium-1 black', {
                'mb-25 text-bold-500': context !== 'order',
              })}
            >
              <FormattedNumber
                value={getOrderItemTotal(checkout?.orderType, orderItem)}
                format="priceCurrency"
              />
            </span>
            {['cart', 'course_cart'].includes(context) &&
              updateCartItem &&
              cart &&
              checkout && (
                <QtySelector
                  value={qty}
                  onClickPlus={() =>
                    updateCartItemQty(
                      updateCartItem,
                      cart,
                      hash,
                      true,
                      checkout,
                    )
                  }
                  onClickMinus={() =>
                    updateCartItemQty(
                      updateCartItem,
                      cart,
                      hash,
                      false,
                      checkout,
                    )
                  }
                />
              )}
          </>
        )}
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state) => ({
  cart: state.store.cart,
  store: state.store.store,
  order: state.store.order,
  checkout: state.store.checkout,
  me: state.me.me,
  paymentMethods: state.me.paymentMethods,
  sharedCarts: state.store.sharedCarts,
  menuEntriesData: state.store.menuEntriesData,
});

export default connect(mapStateToProps, {
  updateCartItem,
  addCartItem,
})(OrderItemLine);
